<script setup lang="ts">
import { storeToRefs } from 'pinia';
import UhSpinner from '~/components/general/uhSpinner/UhSpinner.vue';
import BehindPositionIcon from '~/components/navigation/appSidebar/icons/BehindPositionIcon.vue';
import ChartBarIcon from '~/components/navigation/appSidebar/icons/ChartBarIcon.vue';
import CurrentLineMarker from '~/components/navigation/appSidebar/icons/CurrentLineMarker.vue';
import FlagIcon from '~/components/navigation/appSidebar/icons/FlagIcon.vue';
import RocketLaunchIcon from '~/components/navigation/appSidebar/icons/RocketLaunchIcon.vue';
import StarIcon from '~/components/navigation/appSidebar/icons/StarIcon.vue';
import TrendUpIcon from '~/components/navigation/appSidebar/icons/TrendUpIcon.vue';
import TrophyIcon from '~/components/navigation/appSidebar/icons/TrophyIcon.vue';
import {
  clampPortfolioConversionPosition,
  getOrdersToMatchLastYear,
  getPortfolioConversionCopy,
  getPortfolioConversionCountLabel,
  getPortfolioConversionState,
  getPortfolioConversionTone,
  getPortfolioConversionYtdLabel,
  PORTFOLIO_CONVERSION_TONES,
  showsAheadEndCap,
  showsBehindMarker,
  showsCurrentCap,
  showsGhostMarker,
  showsSolidGhost,
} from '~/components/navigation/appSidebar/portfolioConversionDisplay';
import { useMainStore } from '~/stores/mainStore';

const { portfolioConversion } = storeToRefs(useMainStore());

const formsCount = computed<number>(() => {
  const { totalForms, orderFormsCount } = portfolioConversion.value;
  return totalForms ?? orderFormsCount;
});

const displayState = computed(() =>
  getPortfolioConversionState({
    formsCount: formsCount.value,
    percentage: portfolioConversion.value.percentage,
    previousPercentage: portfolioConversion.value.previousPercentage,
    band: portfolioConversion.value.band,
    livePropertiesCount: portfolioConversion.value.livePropertiesCount,
  }),
);

const ordersToMatchLastYear = computed(() =>
  getOrdersToMatchLastYear(
    formsCount.value,
    portfolioConversion.value.previousPercentage,
    portfolioConversion.value.livePropertiesCount,
  ),
);

const copy = computed(() =>
  getPortfolioConversionCopy(displayState.value, ordersToMatchLastYear.value),
);

const tone = computed(() =>
  PORTFOLIO_CONVERSION_TONES[
    getPortfolioConversionTone(portfolioConversion.value.band, displayState.value)
  ],
);

const percentageLabel = computed<string>(() => {
  if (portfolioConversion.value.percentage === null) {
    return '0%';
  }

  return `${portfolioConversion.value.percentage}%`;
});

const countLabel = computed<string>(() =>
  getPortfolioConversionCountLabel(
    formsCount.value,
    portfolioConversion.value.livePropertiesCount,
  ),
);

const currentPosition = computed<number>(() =>
  clampPortfolioConversionPosition(portfolioConversion.value.percentage),
);

const ghostPosition = computed<number>(() =>
  clampPortfolioConversionPosition(portfolioConversion.value.previousPercentage),
);

const hasGhost = computed<boolean>(() =>
  showsGhostMarker(portfolioConversion.value.previousPercentage),
);

const fillWidth = computed<number>(() => {
  if (formsCount.value === 0) {
    return 0;
  }

  return currentPosition.value;
});

const ytdLabel = computed<string>(() =>
  getPortfolioConversionYtdLabel(displayState.value, formsCount.value),
);
</script>

<template>
  <div
    class="relative mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white p-4 pt-5 shadow-sm dark:border-gray-500 dark:bg-gray-800"
    data-cy="portfolio-conversion-indicator"
  >
    <div
      class="absolute inset-x-0 top-0 h-1"
      :style="{ backgroundColor: tone.accent }"
      data-cy="portfolio-conversion-accent"
    />

    <template v-if="portfolioConversion.loaded">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <p class="text-sm font-bold leading-5 text-slate-900 dark:text-white">
            Order form return rate
          </p>

          <p
            class="text-[48px] font-extrabold leading-none"
            :class="tone.percentageClass"
            data-cy="portfolio-conversion-percentage"
          >
            {{ percentageLabel }}
          </p>

          <p
            class="text-[13px] font-medium leading-[18px] text-slate-500 dark:text-gray-400"
            data-cy="portfolio-conversion-detail"
          >
            {{ countLabel }}
          </p>

          <p
            class="inline-flex w-fit whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold leading-[13px]"
            :class="tone.pillClass"
            data-cy="portfolio-conversion-pill"
          >
            {{ copy.pill }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <div class="flex flex-col gap-1">
            <div
              class="relative h-[18px] w-full"
              role="meter"
              :aria-valuenow="currentPosition"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-label="`${percentageLabel}. ${countLabel}. ${copy.status}`"
              data-cy="portfolio-conversion-bar"
            >
              <div
                class="absolute inset-x-0 top-[5px] h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-600"
              >
                <div
                  class="h-full rounded-full"
                  :style="{
                    width: `${fillWidth}%`,
                    backgroundColor: tone.fill,
                  }"
                  data-cy="portfolio-conversion-fill"
                />
              </div>

              <div
                v-if="showsAheadEndCap(displayState) && formsCount > 0"
                class="pointer-events-none absolute top-0 -translate-x-1/2"
                :style="{ left: `${fillWidth}%`, color: tone.fill }"
                data-cy="portfolio-conversion-end-cap"
              >
                <CurrentLineMarker />
              </div>

              <div
                v-if="hasGhost && showsSolidGhost(displayState)"
                class="pointer-events-none absolute top-0 -translate-x-1/2 text-slate-400 dark:text-gray-400"
                :style="{ left: `${ghostPosition}%` }"
                data-cy="portfolio-conversion-ghost"
              >
                <CurrentLineMarker />
              </div>

              <div
                v-if="showsCurrentCap(displayState) && formsCount > 0"
                class="pointer-events-none absolute top-0 -translate-x-1/2"
                :style="{ left: `${currentPosition}%`, color: tone.fill }"
                data-cy="portfolio-conversion-marker"
              >
                <CurrentLineMarker />
              </div>

              <div
                v-if="showsBehindMarker(displayState)"
                class="pointer-events-none absolute top-[3px] -translate-x-1/2"
                :style="{ left: `${currentPosition}%`, color: tone.fill }"
                data-cy="portfolio-conversion-behind-marker"
              >
                <BehindPositionIcon />
              </div>

            </div>

            <div class="text-[13px] font-medium leading-5">
              <span
                :class="tone.ytdClass"
                data-cy="portfolio-conversion-you-label"
              >
                {{ ytdLabel }}
              </span>
            </div>
          </div>

          <div
            class="flex items-center gap-2 text-[13px] font-medium leading-4"
            :class="tone.statusClass"
            data-cy="portfolio-conversion-status"
          >
            <span class="inline-flex h-4 w-4 shrink-0 items-center justify-center">
              <TrophyIcon v-if="copy.icon === 'trophy'" />
              <ChartBarIcon v-else-if="copy.icon === 'chart-bar'" />
              <TrendUpIcon v-else-if="copy.icon === 'trend-up'" />
              <RocketLaunchIcon v-else-if="copy.icon === 'rocket-launch'" />
              <StarIcon
                v-else-if="copy.icon === 'star'"
                :style="{ color: tone.fill }"
              />
              <FlagIcon v-else-if="copy.icon === 'flag'" />
            </span>
            <span>{{ copy.status }}</span>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <UhSpinner size="8" />
    </template>
  </div>
</template>
